/**
 * Bring Your Own Skill (BYOS) — shared types.
 *
 * A "skill" is a Markdown instruction file (optionally bundled with
 * supporting scripts) that encodes client-specific patterns the Forge
 * agents should follow when planning, writing code, and reviewing.
 * Skills are persisted per project (inherited by every repo in the
 * project) or per repo (overrides the project default for that repo).
 *
 * ## Merge & override semantics
 *
 *   merged(repo)  =  project_skills(active)                              [scope: 'project']
 *                 +  repo_skills(active)                                  [scope: 'repo']
 *
 * If a repo-level skill has the same `slug` as a project-level skill,
 * the repo entry shadows the project entry. The merged list returned
 * by GET /repos/.../skills marks the shadowed project entry with
 * `overridden: true` so the UI can render the badge; the agent
 * orchestrator simply drops the project copy and keeps the repo copy.
 *
 * Order of rendering at agent launch: project-level first, repo-level
 * second. Inactive skills are excluded entirely.
 *
 * ## DDB key shape
 *
 *   Project-level:  PK = PROJECT#<id>          SK = SKILL#<slug>
 *   Repo-level:     PK = REPO#<org>/<repo>     SK = SKILL#<slug>
 *
 * ## S3 layout
 *
 *   Bucket env:  FORGE_SKILLS_BUCKET
 *   Key:         forge-skills/{scope-slug}/{slug}.{ext}
 *      where scope-slug = `project-<id>` or `repo-<org>-<repo>`
 *      and ext = 'md' or 'zip'.
 *
 * The Lambda role gets s3:PutObject / GetObject / DeleteObject on
 * `arn:aws:s3:::<FORGE_SKILLS_BUCKET>/forge-skills/*`.
 *
 * ## Env vars consumed by the Forge orchestrator
 *
 *   CUSTOM_SKILLS_JSON          — see {@link CustomSkillsEnv}
 *   CUSTOM_SKILL_SCRIPTS_JSON   — see {@link CustomSkillScriptsEnv}
 *
 * Both vars are injected by `launchFargateTask()` after the BYOK +
 * MCP resolution step. Total payload is capped at 200 KB; offending
 * skills are truncated largest-first and a warning logged.
 */

export type SkillKind = 'md' | 'bundle';

export type SkillScope = 'project' | 'repo';

/** Slug: kebab-case, 1..63 chars, no leading/trailing dash. */
export const SKILL_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$|^[a-z0-9]$/;

/** Caps enforced by the upload + injection paths. */
export const MAX_SKILL_FILE_BYTES = 512 * 1024; // 512 KB per skill
export const MAX_TOTAL_SKILL_BYTES = 5 * 1024 * 1024; // 5 MB per scope (repo or project)
export const MAX_INJECTED_SKILL_BYTES = 200 * 1024; // 200 KB injected at agent launch
export const INLINE_CONTENT_THRESHOLD = 8 * 1024; // skills smaller than this skip the S3 fetch at launch

// ---------------------------------------------------------------------------
// DDB persistence shape (raw row, snake_case to match the rest of the table)
// ---------------------------------------------------------------------------

export interface SkillDynamoItem {
  PK: string;            // PROJECT#<id> or REPO#<org>/<repo>
  SK: string;            // SKILL#<slug>
  slug: string;
  name: string;
  description?: string | null;
  kind: SkillKind;
  s3_key: string;
  size_bytes: number;
  /** Only set for md-only skills smaller than INLINE_CONTENT_THRESHOLD. */
  content?: string | null;
  active: boolean;
  uploaded_at: string;
  uploaded_by: string | null;
  /** Account that owns this skill — mirrored from the parent PROJECT#/REPO#. */
  account_id?: string | null;
}

// ---------------------------------------------------------------------------
// API view (camelCase, sent to the web)
// ---------------------------------------------------------------------------

export interface Skill {
  slug: string;
  name: string;
  description: string | null;
  kind: SkillKind;
  scope: SkillScope;
  /** PROJECT#<id> or REPO#<org>/<repo> — useful to link back from the merged list. */
  parentPk: string;
  sizeBytes: number;
  active: boolean;
  uploadedAt: string;
  uploadedBy: string | null;
}

/**
 * Item returned by the merged repo-skills list. Same as Skill plus
 * the `overridden` flag, which is true on a project-scope entry that
 * is shadowed by a repo-scope entry with the same slug.
 */
export interface SkillListItem extends Skill {
  overridden: boolean;
}

export interface SkillBundleFile {
  /** Path relative to .forge/skills/<slug>/ — never absolute, never ../-escaped. */
  relPath: string;
  /** Raw bytes; small enough to inline because we already capped at 512 KB total. */
  content: string;
}

/**
 * Preview view — returns the parsed skill.md content + (for bundles)
 * the list of supporting files for the UI's file-tree preview.
 */
export interface SkillPreview extends Skill {
  /** Markdown source (for md-only) or the bundle's skill.md (for bundles). */
  content: string;
  /** Bundle scripts/templates — empty for md-only skills. */
  bundleFiles: SkillBundleFile[];
}

// ---------------------------------------------------------------------------
// Request / response envelopes
// ---------------------------------------------------------------------------

export interface ListSkillsResponse {
  data: {
    skills: SkillListItem[];
    totalBytes: number;
    capBytes: number; // mirrors MAX_TOTAL_SKILL_BYTES for the UI usage indicator
  };
}

export interface GetSkillResponse {
  data: SkillPreview;
}

export interface UploadSkillResponse {
  data: Skill;
}

export interface ToggleSkillRequest {
  active: boolean;
}

export interface ToggleSkillResponse {
  data: Skill;
}

// ---------------------------------------------------------------------------
// Orchestrator env-var contracts (consumed by the Forge orchestrator +
// agent/run.js; defined here so both sides import the same shape)
// ---------------------------------------------------------------------------

/**
 * JSON-encoded payload of every active skill the orchestrator decided
 * to inject for this agent launch. Bundle scripts ride separately in
 * CUSTOM_SKILL_SCRIPTS_JSON; this var only carries the rendered .md
 * content the agent reads at planning time.
 */
export interface CustomSkillsEnv {
  /** Order matters — project-scope items render before repo-scope items. */
  skills: Array<{
    slug: string;
    name: string;
    description: string | null;
    scope: SkillScope;
    /** Rendered markdown body (always present, even for bundles). */
    content: string;
    /** True if the orchestrator truncated this skill to fit under the 200 KB cap. */
    truncated?: boolean;
  }>;
}

/**
 * JSON-encoded payload of bundle scripts to extract into
 * `.forge/skills/<slug>/` inside the worktree during setup, BEFORE
 * the agent's first iteration. Empty array when no bundles are active.
 */
export interface CustomSkillScriptsEnv {
  bundles: Array<{
    slug: string;
    files: SkillBundleFile[];
  }>;
}

// ---------------------------------------------------------------------------
// Validation helpers — exported so backend + web use the same regex.
// ---------------------------------------------------------------------------

/**
 * Derive a slug from a filename: drop the extension, lowercase,
 * collapse non-alphanumerics into single dashes, strip leading/trailing
 * dashes. Returns null if nothing usable remains.
 */
export function slugFromFilename(filename: string): string | null {
  const noExt = filename.replace(/\.(md|zip)$/i, '');
  const slug = noExt
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (!slug || !SKILL_SLUG_PATTERN.test(slug)) return null;
  return slug;
}
